import React, { useState, memo } from 'react';
import { ShieldCheckIcon, CalendarCheckIcon, HomeIcon, BrainIcon, HeartHandshakeIcon, ArrowRightIcon, StarIcon, ClockIcon, UsersIcon, ChevronRightIcon } from 'lucide-react';
export function ServicesSection() {
  const [activeTab, setActiveTab] = useState<'hire' | 'care'>('hire');
  return <section className="w-full bg-white py-16 lg:py-24 border-b border-gray-100" aria-label="Services" data-id="element-437">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-438">
        {/* Section header */}
        <div className="text-center mb-10" data-id="element-439">
          <div className="inline-flex items-center gap-2 bg-soft-teal text-accent-teal-dark text-xs font-semibold px-3 py-1.5 rounded-full mb-4" data-id="element-440">
            <ShieldCheckIcon className="w-3.5 h-3.5" data-id="element-441" />
            Nurse-Led & Clinically Overseen
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-3" data-id="element-442">
            Healthcare Staffing &{' '}
            <span className="text-medical-blue" data-id="element-443">Care Solutions</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto" data-id="element-444">
            Every professional on our platform is vetted and managed by a
            Registered Nurse and former PSW — ensuring clinical standards you
            can trust.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-10" data-id="element-445">
          <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1" data-id="element-446">
            <button onClick={() => setActiveTab('hire')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'hire' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'hire'} data-id="element-447">
              Hire Now
            </button>
            <button onClick={() => setActiveTab('care')} className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeTab === 'care' ? 'bg-medical-blue text-white shadow-sm' : 'text-text-muted hover:text-text-dark'}`} aria-pressed={activeTab === 'care'} data-id="element-448">
              Care Solutions
            </button>
          </div>
        </div>

        {/* Hire Now Grid */}
        {activeTab === 'hire' && <div data-id="element-449">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-450">
              {/* RN Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-451">
                {/* Card top bar */}
                <div className="bg-medical-blue px-6 pt-6 pb-8 relative" data-id="element-452">
                  <div className="flex items-start justify-between mb-4" data-id="element-453">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center" data-id="element-454">
                      <span className="text-white font-extrabold text-xl" data-id="element-455">
                        RN
                      </span>
                    </div>
                    {/* Clinical Oversight Badge */}
                    <span className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-456">
                      <ShieldCheckIcon className="w-3 h-3" data-id="element-457" />
                      Clinical Oversight
                    </span>
                  </div>
                  <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-458">
                    Registered Nurse
                  </h3>
                  <p className="text-white/70 text-sm mt-1" data-id="element-459">
                    Full scope of practice
                  </p>

                  {/* Decorative circle */}
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" data-id="element-460" />
                </div>

                {/* Card body */}
                <div className="px-6 py-5" data-id="element-461">
                  <div className="space-y-3 mb-5" data-id="element-462">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-463">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-464" />
                      <span data-id="element-465">4.9★ avg. rating across 1,200+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-466">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-467" />
                      <span data-id="element-468">Day, evening & overnight shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-469">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-470" />
                      <span data-id="element-471">CNO registered & credential-verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-472">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-473" />
                      <span data-id="element-474">Vetted by our RN clinical team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-475">
                    <div data-id="element-476">
                      <div className="text-xs text-text-muted font-medium" data-id="element-477">
                        Starting from
                      </div>
                      <div className="text-medical-blue font-extrabold text-xl" data-id="element-478">
                        $42
                        <span className="text-sm font-semibold text-text-muted" data-id="element-479">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-480">
                      <div className="text-xs text-text-muted font-medium" data-id="element-481">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-482">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-483" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-484">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-485" />
                    View Availability
                  </button>
                </div>
              </div>

              {/* RPN Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-486">
                <div className="bg-[#6366f1] px-6 pt-6 pb-8 relative" data-id="element-487">
                  <div className="flex items-start justify-between mb-4" data-id="element-488">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center" data-id="element-489">
                      <span className="text-white font-extrabold text-xl" data-id="element-490">
                        RPN
                      </span>
                    </div>
                    <span className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-491">
                      <ShieldCheckIcon className="w-3 h-3" data-id="element-492" />
                      Clinical Oversight
                    </span>
                  </div>
                  <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-493">
                    Registered Practical Nurse
                  </h3>
                  <p className="text-white/70 text-sm mt-1" data-id="element-494">
                    Skilled clinical support
                  </p>
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" data-id="element-495" />
                </div>

                <div className="px-6 py-5" data-id="element-496">
                  <div className="space-y-3 mb-5" data-id="element-497">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-498">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-499" />
                      <span data-id="element-500">4.8★ avg. rating across 890+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-501">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-502" />
                      <span data-id="element-503">Flexible scheduling, all shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-504">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-505" />
                      <span data-id="element-506">CNO/CARNA registered & verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-507">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-508" />
                      <span data-id="element-509">Vetted by our RN clinical team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-510">
                    <div data-id="element-511">
                      <div className="text-xs text-text-muted font-medium" data-id="element-512">
                        Starting from
                      </div>
                      <div className="text-[#6366f1] font-extrabold text-xl" data-id="element-513">
                        $36
                        <span className="text-sm font-semibold text-text-muted" data-id="element-514">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-515">
                      <div className="text-xs text-text-muted font-medium" data-id="element-516">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-517">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-518" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-[#6366f1] hover:bg-[#4f46e5] text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-519">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-520" />
                    View Availability
                  </button>
                </div>
              </div>

              {/* PSW Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float overflow-hidden group" data-id="element-521">
                <div className="bg-accent-teal px-6 pt-6 pb-8 relative" data-id="element-522">
                  <div className="flex items-start justify-between mb-4" data-id="element-523">
                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center" data-id="element-524">
                      <span className="text-white font-extrabold text-xl" data-id="element-525">
                        PSW
                      </span>
                    </div>
                    <span className="flex items-center gap-1 bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30" data-id="element-526">
                      <ShieldCheckIcon className="w-3 h-3" data-id="element-527" />
                      Clinical Oversight
                    </span>
                  </div>
                  <h3 className="text-white font-extrabold text-xl leading-tight" data-id="element-528">
                    Personal Support Worker
                  </h3>
                  <p className="text-white/70 text-sm mt-1" data-id="element-529">
                    Compassionate daily care
                  </p>
                  <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-white/10 rounded-full" data-id="element-530" />
                </div>

                <div className="px-6 py-5" data-id="element-531">
                  <div className="space-y-3 mb-5" data-id="element-532">
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-533">
                      <StarIcon className="w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" data-id="element-534" />
                      <span data-id="element-535">4.7★ avg. rating across 2,100+ shifts</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-536">
                      <ClockIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-537" />
                      <span data-id="element-538">24/7 availability, all regions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-539">
                      <ShieldCheckIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-540" />
                      <span data-id="element-541">Background-checked & insured</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-muted" data-id="element-542">
                      <UsersIcon className="w-4 h-4 text-accent-teal flex-shrink-0" data-id="element-543" />
                      <span data-id="element-544">Vetted by our RN & former PSW team</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 py-3 border-t border-gray-100" data-id="element-545">
                    <div data-id="element-546">
                      <div className="text-xs text-text-muted font-medium" data-id="element-547">
                        Starting from
                      </div>
                      <div className="text-accent-teal font-extrabold text-xl" data-id="element-548">
                        $26
                        <span className="text-sm font-semibold text-text-muted" data-id="element-549">
                          /hr
                        </span>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-550">
                      <div className="text-xs text-text-muted font-medium" data-id="element-551">
                        Available
                      </div>
                      <div className="flex items-center gap-1 text-accent-teal font-bold text-sm" data-id="element-552">
                        <span className="w-1.5 h-1.5 bg-accent-teal rounded-full live-dot" data-id="element-553" />
                        Now
                      </div>
                    </div>
                  </div>

                  <button className="w-full bg-accent-teal hover:bg-accent-teal-dark text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm" data-id="element-554">
                    <CalendarCheckIcon className="w-4 h-4" data-id="element-555" />
                    View Availability
                  </button>
                </div>
              </div>
            </div>

            {/* Nurse-Led USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-556">
              <div className="flex items-center gap-3" data-id="element-557">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-558">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-559" />
                </div>
                <div data-id="element-560">
                  <div className="font-bold text-text-dark text-sm" data-id="element-561">
                    Clinical Oversight on Every Hire
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-562">
                    All workers are screened and managed by our Registered Nurse
                    and former PSW leadership team — not HR generalists.
                  </div>
                </div>
              </div>
              <a href="#why-us" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-563">
                Learn more <ChevronRightIcon className="w-4 h-4" data-id="element-564" />
              </a>
            </div>
          </div>}

        {/* Care Solutions Menu */}
        {activeTab === 'care' && <div data-id="element-565">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-566">
              {/* In-Home Nursing */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-6 group cursor-pointer" data-id="element-567">
                <div className="w-14 h-14 bg-soft-teal rounded-2xl flex items-center justify-center mb-5" data-id="element-568">
                  <HomeIcon className="w-7 h-7 text-medical-blue" data-id="element-569" />
                </div>
                <div className="flex items-center gap-2 mb-2" data-id="element-570">
                  <h3 className="font-extrabold text-text-dark text-lg" data-id="element-571">
                    In-Home Nursing
                  </h3>
                  <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-572">
                    <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-573" />
                    Clinical Oversight
                  </span>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-574">
                  Professional nursing care delivered in the comfort of your
                  home. Wound care, medication management, post-surgical
                  support, and chronic disease monitoring.
                </p>
                <ul className="space-y-2 mb-6" data-id="element-575">
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-576">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-577" />
                    Post-surgical recovery care
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-578">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-579" />
                    Wound care & IV therapy
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-580">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-581" />
                    Medication administration
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-582">
                    <span className="w-1.5 h-1.5 bg-medical-blue rounded-full flex-shrink-0" data-id="element-583" />
                    Chronic disease management
                  </li>
                </ul>
                <button className="w-full border-2 border-medical-blue text-medical-blue hover:bg-medical-blue hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-584">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-585" />
                </button>
              </div>

              {/* Dementia Care */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-6 group cursor-pointer" data-id="element-586">
                <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-587">
                  <BrainIcon className="w-7 h-7 text-purple-600" data-id="element-588" />
                </div>
                <div className="flex items-center gap-2 mb-2" data-id="element-589">
                  <h3 className="font-extrabold text-text-dark text-lg" data-id="element-590">
                    Dementia Care
                  </h3>
                  <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-591">
                    <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-592" />
                    Clinical Oversight
                  </span>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-593">
                  Specialized memory care from workers trained in dementia and
                  Alzheimer's support. Person-centred approaches that preserve
                  dignity and quality of life.
                </p>
                <ul className="space-y-2 mb-6" data-id="element-594">
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-595">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-596" />
                    Alzheimer's & dementia support
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-597">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-598" />
                    Behavioural symptom management
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-599">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-600" />
                    Caregiver respite & relief
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-601">
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" data-id="element-602" />
                    Safe wandering prevention
                  </li>
                </ul>
                <button className="w-full border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-603">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-604" />
                </button>
              </div>

              {/* Personal Care Services */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-float p-6 group cursor-pointer" data-id="element-605">
                <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center mb-5" data-id="element-606">
                  <HeartHandshakeIcon className="w-7 h-7 text-rose-500" data-id="element-607" />
                </div>
                <div className="flex items-center gap-2 mb-2" data-id="element-608">
                  <h3 className="font-extrabold text-text-dark text-lg" data-id="element-609">
                    Personal Care
                  </h3>
                  <span className="flex items-center gap-1 bg-soft-teal text-accent-teal-dark text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-610">
                    <ShieldCheckIcon className="w-2.5 h-2.5" data-id="element-611" />
                    Clinical Oversight
                  </span>
                </div>
                <p className="text-text-muted text-sm leading-relaxed mb-5" data-id="element-612">
                  Compassionate daily living support from vetted PSWs. Bathing,
                  grooming, mobility assistance, meal preparation, and
                  companionship — all with clinical oversight.
                </p>
                <ul className="space-y-2 mb-6" data-id="element-613">
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-614">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-615" />
                    Bathing & personal hygiene
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-616">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-617" />
                    Mobility & transfer assistance
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-618">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-619" />
                    Meal prep & nutrition support
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-muted" data-id="element-620">
                    <span className="w-1.5 h-1.5 bg-rose-400 rounded-full flex-shrink-0" data-id="element-621" />
                    Companionship & social engagement
                  </li>
                </ul>
                <button className="w-full border-2 border-rose-500 text-rose-500 hover:bg-rose-500 hover:text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm" data-id="element-622">
                  Learn More
                  <ArrowRightIcon className="w-4 h-4" data-id="element-623" />
                </button>
              </div>
            </div>

            {/* Care Solutions USP banner */}
            <div className="bg-soft-teal rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-624">
              <div className="flex items-center gap-3" data-id="element-625">
                <div className="w-10 h-10 bg-accent-teal rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-626">
                  <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-627" />
                </div>
                <div data-id="element-628">
                  <div className="font-bold text-text-dark text-sm" data-id="element-629">
                    Every Care Plan is Nurse-Supervised
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-630">
                    Our founder — a Registered Nurse and former PSW — personally
                    oversees care quality standards across all service
                    categories.
                  </div>
                </div>
              </div>
              <a href="#why-us" className="flex-shrink-0 flex items-center gap-1.5 text-medical-blue font-bold text-sm hover:underline" data-id="element-631">
                Our approach <ChevronRightIcon className="w-4 h-4" data-id="element-632" />
              </a>
            </div>
          </div>}
      </div>
    </section>;
}